import * as Yup from 'yup';
import { useFormik } from "formik";
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Form, Button, Label, Header, Icon } from 'semantic-ui-react';
import { formatPhoneNumbers } from '../_helpers/formatter';
import { 
    alertService,
    facilityService,
    providerService,
    userService,
    customerService
} from '../_services';

function AddEdit({ history, match }) {
    const { id } = match.params;
    const pathname = window.location.pathname;
    const [isAddMode, setIsAddMode] = useState(!id);
    const [isLoading, setLoading] = useState(false);
    const [siteOptions, setSiteOptions] = useState([]);
    const [providerOptions, setProviderOptions] = useState([]);
    const [currentSelectedProviders, setSelectedProviders] = useState([]);      // used purely to display all selected providers
    const [currentSelectedFacilities, setSelectedFacilities] = useState([]);    // used purely to display all selected facilities

    // View mode
    const [viewMode, setViewMode] = useState(false);

    // form validation rules
    const validationSchema = Yup.object().shape({
        first_name: Yup.string()
            .required('First Name is required'),
        last_name: Yup.string()
            .required('Last Name is required'),
        email: Yup.string()
            .required('Email is required'),
        phone_number: Yup.string()
            .max(12, 'Phone number must be no more than 12 digits long')
            .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Must be in the format xxx-xxx-xxxx'),
        external_user_id: Yup.string()
            .max(10, 'Can be no more than 10 characters'),
    });

    function generateShortId(length = 8) {
        return Math.random().toString(36).substring(2, length+2);
    }
    
    const formik = useFormik({
        initialValues: {
            first_name: "",
            last_name: "",
            email: "",
            phone_number: "",
            external_user_id: "",
            current_facilities: [],
            facilities_to_assign: [],
            facilities_to_unassign: [],
            current_providers:  [],
            providers_to_assign: [],
            providers_to_unassign: [],
            role_name: "DESIGNATED_CONTACT",
        },
        validationSchema: validationSchema,
        onSubmit: handleSubmit
    });

    function handleSubmit (data) {
        var submissionData = JSON.parse(JSON.stringify(data));
        delete submissionData.current_facilities;
        delete submissionData.current_providers;
        if(!submissionData.external_user_id){
            submissionData.external_user_id = generateShortId();
        }
        
        return isAddMode
        ? createContact(submissionData)
        : updateContact(id, submissionData);
    }

    function handleResendLoginInfo() {
        const user_email = formik.values.email;
        return customerService.resendLoginInfo({ user_email: user_email })
        .then((response) => {
            alertService.success('Login info sent', { keepAfterRouteChange: true });
            history.push('/designated_contacts');
        })
        .catch((err) => {
            alertService.error(err);
        });
    }

    const createContact = (data) => {
        userService.createDesignatedContact(data)
        .then((resp) => {
            formik.setSubmitting(false);
            history.push('/designated_contacts');
            alertService.success('Contact created.');
        }).catch((error) => {
            if(error.toString().includes('Failed to fetch')){
                alertService.error('Server cannot be reached.');
            }else{
                alertService.error(error);
            }
            formik.setSubmitting(false);
        });
    }

    const updateContact = (id, data) => {
        delete data.role_name;
        userService.updateDesignatedContact(id, data)
        .then((resp) => {
            alertService.success('Contact updated', {keepAfterRouteChange: true});
            history.push('/designated_contacts');
        }).catch((error) => {
            formik.setSubmitting(false);
            alertService.error(error);
        });
    }

    /**
    * Filters out the user from a given array and sets formik value.
    * @param {string[]} listToFilter - array to be filtered.
    * @param {string} fieldToUpdate - field in formik to update.
    * @param {obj} user - The user obj you want to filter out by id.
    */
    const filterObjOutFormik = (listToFilter, fieldToUpdate, user) => {
        const filteredList = listToFilter.filter((obj) => obj != user.id);
        formik.setFieldValue(fieldToUpdate, filteredList);
        return filteredList;
    }

    const unAssignProvider = async (provider) => {
        // Always remove from the UI
        const listWithRemovedProvider = currentSelectedProviders.filter(function(prov) {
            return prov.id !== provider.id;
        });
        setSelectedProviders(listWithRemovedProvider);

        // If currently assigned in the db then we add to unassign list
        const assignedInDb = formik.values.current_providers.some((val) => {
            return val.id == provider.id;
        });
        if(assignedInDb & !isAddMode){
            console.log('Unassigning provider');
            formik.setFieldValue('providers_to_unassign', [...formik.values.providers_to_unassign, provider.id]);
        }
        // If in to_assign list then remove it
        filterObjOutFormik(formik.values.providers_to_assign, 'providers_to_assign', provider);
    }

    function assignProvider (key, options) {
        const selectedProvider = options.find((prov) => prov.key === key);

        // If not already selected update UI
        const alreadySelected = currentSelectedProviders.some((dict, indx) => dict['id'] == key);
        
        if(!alreadySelected){
            currentSelectedProviders.push(selectedProvider.data);
            setSelectedProviders(currentSelectedProviders);

            //If not already assigned in db then add to assign listd
            const alreadyAssigned = formik.values.current_providers.some((val) => val.id === key);
            if(!alreadyAssigned){
                    const to_assign_list = [...formik.values.providers_to_assign, selectedProvider.key];
                    formik.setFieldValue('providers_to_assign', to_assign_list);
            }
        }
        // If in unassign list then remove
        filterObjOutFormik(formik.values.providers_to_unassign, 'providers_to_unassign', selectedProvider);
    }

    const assignFacility = (key, options) => {
        const selectedFac = options.find((fac) => fac.key === key);

        //If not already selected update UI
        const alreadySelected = currentSelectedFacilities.some((dict) => dict.id === key);
        if(!alreadySelected){
            currentSelectedFacilities.push(selectedFac);
            setSelectedFacilities(currentSelectedFacilities);

            const alreadyAssigned = formik.values.facilities_to_assign.some((id) => id === key);
            if(!alreadyAssigned){
                let to_assign_list = [...formik.values.facilities_to_assign, selectedFac.id];
                formik.setFieldValue('facilities_to_assign', to_assign_list);
            }
        }
        // If in unassign list then remove
        filterObjOutFormik(formik.values.facilities_to_unassign, 'facilities_to_unassign', selectedFac);
    }

    const unAssignSite = (facility) => {
        // Remove from the UI list
        const listWithRemovedProvider = currentSelectedFacilities.filter((fac) => fac.id !== facility.id);
        setSelectedFacilities(listWithRemovedProvider);

        // If currently assigned in the db then we add to unassign list
        const assignedInDb = formik.values.current_facilities.some((fac) => fac.id === facility.id);
        if(assignedInDb & !isAddMode){
            formik.setFieldValue('facilities_to_unassign', [...formik.values.facilities_to_unassign, facility.id]);
        }

        // If in to_assign list then remove it
        filterObjOutFormik(formik.values.facilities_to_assign, 'facilities_to_assign', facility);
    }

    useEffect(() => {
        if (pathname.includes("view")) {
            setViewMode(true);
        } else {
            setViewMode(false);
        }
    }, [pathname]);

    // Loading wheel for edit/view
    useEffect(() => {
        if (window.onload || isAddMode) {
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [window]);

    // Load Facility and Provider Options
    useEffect(() => {
        // Load options for facilities and providers
        facilityService.getAllSites()
        .then((resp) => {
            let sitesArr = [];
            resp.data.map((site, indx) => {
                sitesArr.push(
                    {
                        key: site.id,
                        text: `${site.name}`,
                        value: site.id,
                        name: site.name,
                        id: site.id,
                    }
                )
            });
            setSiteOptions(sitesArr);
        }).catch((error) => {
            alertService.error(error);
            setSiteOptions([]);
        });

        providerService.getAll()
        .then((resp) => {
            let provArr = [];
            resp.data.map((provider, indx) => {
                provArr.push(
                    {
                        ind: indx,
                        key: provider.id,
                        text: provider.name,
                        value: provider.id,
                        data: provider 
                    }
                )
            });
            setProviderOptions(provArr);
        }).catch((error) => {
            alertService.error(error);
            setProviderOptions([]);
        });

        //If in View Mode then load current contact details
        if(!isAddMode){
            userService.getContactById(id)
            .then((resp) => {
                formik.setValues((prevValues) => ({
                    ...prevValues,
                    current_providers: resp.providers,
                    current_facilities: resp.facilities,
                    first_name: resp.first_name,
                    last_name: resp.last_name,
                    email: resp.email,
                    external_user_id: resp.external_user_id,
                    phone_number: resp.phone_number
                }));
                setSelectedFacilities([...resp.facilities]);
                setSelectedProviders([...resp.providers]);
                formik.validateForm();
            })
            .catch((error) => {
                alertService.error(error);
            });
        }else{
            formik.validateForm();
        }
        setLoading(false);
    }, []);

    return (
        <Form className="pl-5" loading={isLoading} onSubmit={formik.handleSubmit}>
        <Header as='h3' style={{paddingTop:'20px', color:'#3F2A57'}}>Designated Contact Details <Icon style={{paddingLeft: '10px', color:'#3F2A57'}} name='user md'/></Header>
            <div className="mt-4">
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Contact First Name"
                        placeholder="First Name"
                        name="first_name"
                        onChange={formik.handleChange}
                        error={formik.errors.first_name}
                        value={formik.values.first_name}
                        width={3}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Contact Last Name"
                        placeholder="Last Name"
                        name="last_name"
                        onChange={formik.handleChange}
                        error={formik.errors.last_name}
                        value={formik.values.last_name}
                        width={3}
                    />
                    <Form.Input 
                        fluid
                        readOnly={viewMode}
                        label="Contact Email"
                        placeholder="Email"
                        name="email"
                        onChange={formik.handleChange}
                        error={formik.errors.email}
                        value={formik.values.email} 
                        width={3}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="User Id"
                        placeholder="User Id"
                        name="external_user_id"
                        onChange={formik.handleChange}
                        error={formik.errors.external_user_id}
                        value={formik.values.external_user_id}
                        width={3}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Contact Phone"
                        placeholder="xxx-xxx-xxxx"
                        name="phone_number"
                        onChange={formik.handleChange}
                        error={formik.errors.phone_number}
                        value={formik.values.phone_number}
                        width={3}
                    />
                </Form.Group>
                <br/>
                <Header as='h3' style={{paddingTop:'20px', color:'#3F2A57'}}>Assigned Facilities<Icon style={{paddingLeft: '10px', color:'#3F2A57'}} name='hospital outline'/></Header>
                <div>
                    <Form.Group inline>
                    <Form.Select
                        fluid
                        selection
                        readOnly={viewMode} 
                        disabled={viewMode}
                        placeholder="Assign Contact to Facility"
                        name="facility_id"
                        options={siteOptions}
                        selectOnNavigation={false}
                        onChange={(event, data) => {assignFacility(data.value, data.options)}}
                        error={formik.errors.facilities}
                        value={{label:{}}}
                        width={6}
                    />
                    </Form.Group>
                    <div>
                        {/* This is for add mode */}
                        {currentSelectedFacilities.map(facility =>
                            <Label onClick={ (event, data) => !viewMode && unAssignSite(data.site)}
                                key={facility.id}
                                site={facility}
                                disabled={viewMode}
                                basic
                                value={facility.name}
                                size={'large'}
                                style={{color:'#3F2A57'}}>
                                    {facility.name} {!viewMode && <Icon style={{paddingLeft:'10px'}} name='delete' />}
                            </Label>)
                        }
                    </div>
                </div>

                <Header as='h3' style={{paddingTop:'20px', color:'#3F2A57'}}>Assigned Providers<Icon style={{paddingLeft: '10px', color:'#3F2A57'}} name='hospital outline'/></Header>
                <div>
                    <Form.Group inline>
                    <Form.Select
                        fluid
                        selection
                        readOnly={viewMode} 
                        disabled={viewMode}
                        placeholder="Assign Contact to Provider"
                        name="provider_id"
                        value={{label:{}}}
                        options={providerOptions}
                        selectOnNavigation={false}
                        onChange={(event, data) => {assignProvider(data.value, data.options)}}
                        width={6}
                    />
                    </Form.Group>
                    <div>
                        {currentSelectedProviders.map(provider =>
                            <Label 
                                onClick={(event, data) => !viewMode && unAssignProvider(data.provider)}
                                key={provider.id}
                                provider={provider}
                                disabled={viewMode}
                                basic
                                value={provider.name}
                                size={'large'}
                                style={{color:'#3F2A57'}}
                            >
                                {`${provider.name}`} {!viewMode && <Icon style={{paddingLeft:'10px'}} name='delete' />}
                            </Label>
                        )}
                    </div>
                </div>
            </div>
            

            <div className="pt-5 mt-4 pl-2 pb-5">
                <Form.Group>
                    <span>
                        <>{!formik.isValid?<Label color='red' content={`There are Invalid Fields - Please Update`}/>:<></>}</>
                    </span>
                </Form.Group>
                <Form.Group>
                    <div className="pr-3">
                        <Button 
                            type="submit"
                            primary
                            onSubmit={formik.handleSubmit}
                            disabled={formik.isSubmitting || viewMode || !formik.isValid}  
                        >
                            {formik.isSubmitting && <span className="spinner-border spinner-border-sm mr-1"></span>}
                            Submit
                        </Button>
                    </div>
                    <Button secondary as={ Link } name='cancel' to='/designated_contacts'>Cancel</Button>
                    {!isAddMode &&
                    <Button
                        className="ml-3"
                        type="button" 
                        name="resend_button"
                        disabled={!viewMode}
                        onClick={handleResendLoginInfo}
                        >Resend Login Info
                    </Button>
                    }
                </Form.Group>
            </div>

        </Form>
    );
};

export {AddEdit}